import type { SiteConfig } from "@/types/sites";
import type { AnalyticsConfig } from "@/types/config";
import type { Variants } from "framer-motion";

// Animation variants
export const fadeInUp: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

export const fadeIn: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
};

export const slideInLeft: Variants = {
  hidden: { opacity: 0, x: -50 },
  visible: { opacity: 1, x: 0 },
};

export const slideInRight: Variants = {
  hidden: { opacity: 0, x: 50 },
  visible: { opacity: 1, x: 0 },
};

export const scaleIn: Variants = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: { opacity: 1, scale: 1 },
};

export const staggerContainer: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

// Default configuration
export const defaultAnalyticsConfig: AnalyticsConfig = {
  enabled: true,
  implementation: "pro",
  respectConsent: true,
  events: {
    page_view: true,
    view_section: true,
    click_cta: true,
    click_element: true,
    generate_lead: true,
    scroll: true,
    web_vital: true,
  },
};

export const defaultConfig: SiteConfig = {
  theme: {},
  layout: {
    header: {
      // Block configuration - defaults to navbar-01 if not specified
      blockConfig: {
        blockType: "navbar-01",
      },

      // Global visibility
      enabled: true,

      // Header positioning
      position: "fixed",

      // Logo configuration
      showLogo: true,
      logoSize: "md",
      logoType: "text",

      // Navigation configuration
      showNavigation: true,
      navigationItems: [],

      // Mobile configuration
      mobileBreakpoint: "md",
      showMobileMenu: true,

      // Language selector configuration
      showLanguageSelector: true,
      defaultLanguage: "en",
      visibleLanguages: ["en", "he"],

      // Action buttons configuration
      showActionButtons: true,
      maxActionButtons: 2,
      actionButtons: [],

      // Optional features
      searchEnabled: false,
      notificationsEnabled: false,
      userMenuEnabled: false,
    },
    hero: {
      blockConfig: {
        blockType: "classic-centered",
      },
      overlay: true,
      backgroundOpacity: 0.9,
      showSubtitle: true,
      showDescription: false,
      showImage: true,
      showButtons: true,
      showBadge: true,
      showStats: true,
      showSocialProof: true,
      showAnnouncement: true,
      showEmphasisKeywords: true,
      showRole: true,
      showBackground: true,
    },
    about: {
      // Block configuration - blocks are the only rendering path
      blockConfig: {
        blockType: "modern-card", // Default block type
      },

      // Visibility toggles - Control what elements appear
      showImage: true,
      showQuote: true,
      showKeyPoints: true,
      showBadge: true,
      showStats: true,
      showSocialProof: true,
      showAnnouncement: true,
      showAchievements: true,
      showValues: true,
      showKeyPointsTitle: true,
      showValuesTitle: true,
    },
    experience: {
      // Block system
      blockConfig: {
        enabled: true,
        blockType: "editorial",
      },

      // Title configs (blocks use these)
      careerHighlightsTitleConfig: undefined,
      certificationsTitleConfig: undefined,
      workingPreferencesTitleConfig: undefined,

      // Card content configs (blocks use these)
      cardContentConfig: undefined,
      certificationsCardContentConfig: undefined,
      careerHighlightsCardContentConfig: undefined,
      workingPreferencesCardContentConfig: undefined,

      // Panel visibility flags
      showBadge: true,
      showSummary: true,
      showCareerHighlights: true,
      showDownloadResume: true,
      showEndorsements: false,
      showCertifications: true,
      showWorkingPreferences: true,

      // Item visibility flags
      showRole: true,
      showCompany: true,
      showDuration: true,
      showDescription: true,
      showLocation: true,
      showTags: true,
      showEmploymentType: true,
      showAchievements: true,
      showProjectHighlights: false,
      showReferences: false,
      showCompanyDescription: false,
      showTeamSize: false,
      showTechnologies: true,

      // Tag style
      tagStyle: "pill",

      // Data preparation
      groupByCompany: false,
      enableFiltering: false,
      filterByEmploymentType: false,
      filterByTechnology: false,
      sortOrder: "newest",
    },
    skills: {
      // Block configuration (blocks are default - no 'enabled' field)
      blockConfig: {
        blockType: null,
      },

      // Title configurations (optional)
      titleConfig: undefined,
      introTextConfig: undefined,
      certificationsTitleConfig: undefined,

      // Card Content Configurations (undefined by default)
      skillHighlightsCardContentConfig: undefined,
      skillsListCardContentConfig: undefined,
      certificationsCardContentConfig: undefined,

      // Section visibility flags
      showSkillsSummary: true,
      showStats: true,
      showSkillCategories: true,
      showGeneralSkills: true,
      showSkillHighlights: true,
      showCertifications: true,
      showLearningGoals: true,
      showAdditionalContent: true,

      // Category visibility flags - Keep for future use
      showCategoryIcons: true,
      showCategoryDescriptions: true,

      // Skill item visibility flags - Keep for future use
      showSkillIcons: true,
      showSkillDescriptions: true,
      showSkillTags: true,
      showSkillLevel: true,
      showProgressPercentageText: true,
    },
    projects: {
      // Block configuration (blocks are default - no 'enabled' field)
      blockConfig: {
        blockType: null,
      },

      // Title & Description Configuration (optional)
      titleConfig: undefined,
      descriptionConfig: undefined,

      // Core project field visibility flags
      showTitle: true,
      showDescription: true,
      showImage: true,
      showTags: true,
      showProjectUrl: true,
      showRepoUrl: false,
      showContribution: false,
      showDate: false,
      showClient: false,
      showCategory: false,
      showCaseStudyUrl: false,

      // Project metadata visibility flags
      showDuration: false,
      showTeamSize: false,
      showComplexity: false,
      showImpact: false,
      showStatus: false,
      showPriority: false,
      showDemoUrl: false,
      showMetrics: false,
      showAwards: false,
      showChallenges: false,
      showLearnings: false,
      showTestimonial: false,
      showVideoUrl: false,
      showGallery: false,

      // Section component visibility flags
      showBadge: true,
      showIntroText: true,
      showPortfolioSummary: true,
      showPortfolioHighlights: true,
      showFeaturedProjects: true,
      showFilterTags: false,
      showProjectCategories: false,
      showCollaboration: false,
      showSectionTechnologies: false,
      showPreferredProjectTypes: false,
      showAchievements: false,
      showClients: false,

      // Card Content Configurations (undefined by default)
      featuredProjectsCardContentConfig: undefined,
      portfolioHighlightsCardContentConfig: undefined,
      portfolioSummaryCardContentConfig: undefined,
      projectItemCardContentConfig: undefined,
    },
    contact: {
      // Block configuration (removed 'enabled' field - blocks are default)
      blockConfig: {
        blockType: null,
      },

      // Title & Subtitle Configuration (optional)
      titleConfig: undefined,
      subtitleConfig: undefined,

      // Core Visibility Toggles
      showContactInfo: true,
      showSocialLinks: true,
      showContactForm: false, // Defaulting form to false as it's more complex
      showMap: false,
      showCustomHtml: false,
      showBadge: true,
      showAvailabilityStatus: true,
      showResponseTime: true,
      showContactMethods: true,
      showCallToAction: false,
      showContactPreferences: false,
      showOfficeInfo: false,
      showVirtualOffice: false,

      // Future Implementation Toggles (kept even if unused)
      showTestimonialQuote: true,
      showAchievements: true,
      showWorkingProcess: true,
      showTrustSignals: false,
    },
    social: {
      blockConfig: {
        blockType: "social-grid-cards",
      },
      showHeading: false,
      heading: "Connect",
      showSubheading: false,
      subheading: "Follow me on social media",
      showLabels: false,
      showHandles: false,
      showFollowerCounts: false,
      iconSize: "medium",
      colorScheme: "brand",
    },
    features: {
      // Block configuration (blocks are default - no 'enabled' field)
      blockConfig: {
        blockType: null,
      },

      // Title & Subtitle Configuration (optional)
      titleConfig: undefined,
      descriptionConfig: undefined,

      // Visibility Toggles
      showIcons: true,
      showBadges: true,
    },
    testimonials: {
      // Block configuration - blocks are now the default implementation
      blockConfig: {
        blockType: null,
      },
      // Title and subtitle configs for consistent typography
      titleConfig: undefined,
      subtitleConfig: undefined,
      // Display limits for content control
      displayLimits: undefined,
      // Feature toggles for optional components
      sections: {
        badge: undefined,
        summary: undefined,
        featuredTestimonial: undefined,
        trustIndicators: undefined,
        callToAction: undefined,
        socialProof: undefined,
        testimonialsFilter: undefined,
      },
    },
    services: {
      blockConfig: {
        blockType: null,
      },
      // Visibility Toggles
      showPricing: true,
      showFeatures: true,
      showIcons: true,
      showAvailability: true,
    },
    faq: {
      blockConfig: {
        blockType: null,
      },
      showCategories: true,
      showSearch: true,
      showContactPrompt: true,
      showAvatar: false,
      showRelatedQuestions: false,
    },
    pricing: {
      blockConfig: {
        blockType: null,
      },
      showBillingToggle: true,
      showMoneyBack: true,
      showAcceptedPayments: true,
      showCurrency: true,
      showPeriod: true,
      currencyPosition: "prefix" as const,
    },
    team: {
      // Block selector (blocks are default rendering method)
      blockConfig: {
        blockType: "team-grid",
      },

      // Visibility toggles
      showRole: true,
      showBio: true,
      showSpecialties: true,
      showSocial: true,
      showAvailability: true,
      showHiringCta: true,

      // Global styling (optional)
      titleConfig: undefined,
      descriptionConfig: undefined,
    },
    stats: {
      layout: "row",
      alignment: "center",
      columns: 3,
      cardStyle: "elevated",
      valueSize: "xl",
      valueStyle: "bold",
      labelStyle: "normal",
      showIcons: true,
      iconPosition: "top",
      iconSize: "md",
      animated: true,
      // animationDuration: 0.5,
      showTrends: true,
      trendStyle: "badge",
      backgroundColor: "#f0f0f0",
      textColor: "#333333",
    },
    blog: {
      layout: "grid",
      alignment: "center",
      columns: 3,
      cardStyle: "elevated",
      cardHoverEffect: "lift",
      imagePosition: "top",
      imageAspectRatio: "16:9",
      showExcerpt: true,
      excerptLength: "medium",
      showAuthor: true,
      authorStyle: "both",
      showDate: true,
      dateFormat: "relative",
      showReadTime: true,
      showCategory: true,
      categoryStyle: "badge",
      showTags: true,
      tagsLimit: 5,
      featuredStyle: "large",
      showFilters: true,
      filterStyle: "tabs",
    },
    gallery: {
      // Block configuration - blocks are the default implementation
      blockConfig: {
        blockType: "gallery-01",
      },
      // Title configs for consistent heading styling across all blocks
      titleConfig: {
        as: "h2" as const,
        fontSizeScale: "3xl",
        fontWeight: "bold",
        align: "center" as const,
        className: "",
      },
      subtitleConfig: {
        as: "h3" as const,
        fontSizeScale: "xl",
        fontWeight: "semibold",
        align: "center" as const,
        className: "",
      },
      descriptionConfig: {
        as: "p" as const,
        fontSizeScale: "lg",
        fontWeight: "normal",
        align: "center" as const,
        className: "",
      },
    },
    timeline: {
      // Block configuration - blocks are the default implementation
      blockConfig: {
        blockType: "vertical-modern",
      },

      // Visibility toggles - used by TimelineCard component
      showImages: true,
      showDate: true,
      showIcons: true,
      showTags: false,
      showStatus: false,

      // Status badge colors
      statusColors: {
        completed: "#10b981",
        "in-progress": "#3b82f6",
        planned: "#6b7280",
      },

      // Typography configuration
      titleConfig: {
        as: "h2" as const,
        fontSizeScale: "3xl",
        fontWeight: "bold",
        align: "center" as const,
        className: "",
      },
      subtitleConfig: {
        as: "h3" as const,
        fontSizeScale: "xl",
        fontWeight: "semibold",
        align: "center" as const,
        className: "",
      },
      descriptionConfig: {
        as: "p" as const,
        fontSizeScale: "lg",
        fontWeight: "normal",
        align: "center" as const,
        className: "",
      },
    },
    ctaBanner: {
      layout: "hero",
      alignment: "center",
      backgroundType: "gradient",
      backgroundOpacity: 0.9,
      textColor: "#ffffff",
      padding: "lg",
      ctaStyle: "buttons",
      buttonSize: "lg",
      buttonStyle: "solid",
      showCountdown: true,
      countdownStyle: "inline",
      countdownPosition: "above",
      showBadges: true,
      badgeStyle: "icons",
    },
    partners: {
      layout: "grid",
      alignment: "center",
      columns: 5,
      logoHeight: "md",
      grayscale: true,
      hoverEffect: "color",
      showDescription: true,
      descriptionStyle: "tooltip",
      autoScroll: true,
      scrollSpeed: "medium",
      gap: "md",
      showFeatured: true,
      featuredStyle: "larger",
    },
    newsletter: {
      layout: "inline",
      alignment: "center",
      formStyle: "simple",
      fieldLayout: "horizontal",
      buttonPosition: "below",
      buttonFullWidth: true,
      buttonSize: "md",
      showBenefits: true,
      benefitsStyle: "list",
      showPrivacy: true,
      privacyPosition: "checkbox",
      showCount: true,
      countPosition: "above",
      showIncentive: true,
      incentivePosition: "right",
      incentiveStyle: "card",
    },
    footer: {
      // Block Configuration (no enabled field - blocks are the default)
      blockConfig: {
        blockType: "footer-component-01", // Default to first block
      },

      // Core Visibility Toggles
      showBrand: true,
      showColumns: true,
      showSocialLinks: true,
      showContactInfo: true,
      showCopyright: true,
      showLegal: true,
      showBackToTop: true,
      showNewsletter: true,
      showDividers: true,

      // Essential Fields (Used by block helpers)
      brandType: "text",
      showBrandDescription: true,
      darkMode: true,
      copyrightFormat: "detailed",

      // Content Presentation Field
      legalSeparator: "pipe",
    },
  },
  visibility: {
    hero: true,
    about: true,
    experience: true,
    skills: true,
    projects: true,
    contact: true,
    social: true,
    features: true,
    testimonials: true,
    faq: true,
    services: true,
    pricing: true,
    team: true,
    gallery: true,
    timeline: true,
  },
  sectionOrder: [
    "hero",
    "about",
    "experience",
    "skills",
    "projects",
    "gallery",
    "timeline",
    "contact",
    "social",
    "features",
    "testimonials",
    "faq",
    "services",
    "pricing",
    "team",
  ],
  alternateBackgrounds: {
    enabled: false, // Default to disabled
    primaryBackground: {
      type: "solid",
      value: "#ffffff",
      opacity: 1,
    },
    alternateBackground: {
      type: "gradient",
      value: "subtle", // References theme.gradients.subtle
      opacity: 1,
    },
  },
  animations: {
    enabled: true,
    heroHeaderEnabled: true,
    duration: 0.3,
    stagger: 0.03,
    easing: "easeInOut",
  },
  typography: {
    bodyFont: "Inter",
    bodyFontWeights: ["400", "700"],
    bodyFontSubsets: ["latin"],
    scale: 1,
    lineHeight: {
      heading: 1.2,
      body: 1.6,
    },
  },
  images: {
    quality: 90,
    lazyLoad: true,
    placeholder: "blur",
  },
  analytics: defaultAnalyticsConfig,
};
